import React from "react";
import { createPortal } from "react-dom";
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import { CalendarDays, ChevronDown } from "lucide-react";
import { DispatchDayCalendarPopover } from "../resultsPageChrome/DispatchDayCalendarPopover.jsx";
import useDispatchDayCalendarContext from "../../hooks/useDispatchDayCalendarContext.js";
import usePopoverDismiss from "../../hooks/usePopoverDismiss.js";
import { getReactPortalRoot } from "../../utils/reactPortalRoot.js";
import { focusCalendarDay, formatReadableDispatchDate, isDispatchDateOutsideRange } from "../../utils/dispatchDayCalendarUtils.js";

export default function GeotabDayPicker({ value, onChange, label = "Calendrier Geotab", disabled = false, min, max }) {
  const [open, setOpen] = React.useState(false);
  const trigger = React.useRef(null);
  const dayButtonRefs = React.useRef([]);
  const popoverId = React.useId();
  const titleId = React.useId();
  const isOpen = open && !disabled;
  const { activeMonthKey, controls, errorMessage, handleSelectMonthKey, handleToggleArchive,
    isLoading, loadingMessage, showArchive } = useDispatchDayCalendarContext({
    activeDate: value, forceReloadOnOpen: true, isOpen,
  });
  const { refs, floatingStyles, isPositioned } = useFloating({
    open: isOpen, placement: "bottom-start",
    middleware: [offset(8), flip({ padding: 12 }), shift({ padding: 12, crossAxis: true })], whileElementsMounted: autoUpdate,
  });
  const close = React.useCallback(({ restoreFocus = false } = {}) => {
    setOpen(false);
    if (restoreFocus) trigger.current?.focus({ preventScroll: true });
  }, []);
  usePopoverDismiss({ anchorElement: trigger.current, floatingRef: refs.floating, isOpen,
    onClose: () => close({ restoreFocus: true }) });
  React.useEffect(() => { setOpen(false); }, [value, disabled]);
  React.useEffect(() => {
    if (!isOpen || !isPositioned || isLoading || errorMessage) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const buttons = dayButtonRefs.current.filter((button) => button && !button.disabled);
      const active = buttons.find((button) => button.getAttribute("aria-current") === "date");
      focusCalendarDay(active || buttons[0]);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isOpen, isPositioned, isLoading, errorMessage, activeMonthKey, controls, min, max]);

  return <>
    <button ref={(node) => { trigger.current = node; refs.setReference(node); }} type="button"
      className="inline-flex h-7 items-center justify-center gap-1 rounded-[4px] border border-slate-300 bg-white px-2 text-xs font-semibold text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-2 focus-visible:outline-accent disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 disabled:shadow-none"
      disabled={disabled} aria-haspopup="dialog" aria-expanded={isOpen} aria-controls={isOpen ? popoverId : undefined}
      aria-label={`${label} : ${formatReadableDispatchDate(value)}`} onClick={() => setOpen((current) => !current)}>
      <CalendarDays size={16} aria-hidden="true" /><span>{value}</span><ChevronDown size={14} aria-hidden="true" />
    </button>
    {isOpen ? createPortal(<DispatchDayCalendarPopover
      activeDate={value} activeMonthKey={activeMonthKey} controls={controls} dayButtonRefs={dayButtonRefs}
      errorMessage={errorMessage} loadingMessage={loadingMessage} minDate={min} maxDate={max}
      floatingStyles={floatingStyles} isOpen isPositioned={isPositioned} positioning="floating" setFloatingRef={refs.setFloating}
      onClose={close} onSelectDate={(date) => {
        if (isDispatchDateOutsideRange(date, { minDate: min, maxDate: max })) return;
        onChange(date);
        close({ restoreFocus: true });
      }} onSelectMonthKey={handleSelectMonthKey} onToggleArchive={handleToggleArchive}
      popoverClassName="max-h-[calc(100dvh-1.5rem)] overflow-y-auto" popoverId={popoverId} titleId={titleId} showArchive={showArchive}
    />, getReactPortalRoot()) : null}
  </>;
}
